import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Boxes, ChevronDown, Eye, Palette, SlidersHorizontal, TestTube2 } from 'lucide-react'
import type { DesignSystem } from '@cogo/kg-repository'
import { BriefingPanel } from '@/features/design-system/briefing/briefing-panel'
import { DesignSystemPage as DesignSystemWorkbench } from '@/features/design-system/components/design-system-page'
import { WorkspaceDesignSystemsList } from '@/features/design-system/components/workspace-design-systems-list'
import {
  useDesignSystemInventory,
  type DesignSystemInventorySummary,
} from '@/features/design-system/design-system-inventory'
import { useDesignSystem } from '@/features/design-system/hooks/use-design-system'
import {
  AXIS_KEYS,
  type DesignSystemState,
} from '@/features/design-system/lib/design-system-options'
import { parseDesignSystemState } from '@/features/design-system/lib/theme-tokens'
import { UiBlockWorkbench, type UiBlockRef } from '@/features/design-system/ui-blocks'
import { adviseAxes } from '@/features/design-system/source-derived/advise'
import { AxisResolutionList } from '@/features/design-system/source-derived/axis-resolution-list'
import type { DesignAxesCopy } from '@/features/design-system/source-derived/axes-copy'
import { deriveFromSource } from '@/features/design-system/source-derived/capability'
import { deriveAxes } from '@/features/design-system/source-derived/derive-axes'
import { loadCurrentProjectSourceTokens } from '@/features/design-system/source-derived/load-current-project-source-tokens'
import type { AxisName, SourceDerivedDesignSystem } from '@/features/design-system/source-derived/types'
import { ValidatorPanel } from '@/features/design-system/validator/validator-panel'
import { readVerifiedDesignLibraries } from '@/features/design-system/verified-libraries/read-verified-design-libraries'
import { VerifiedLibrariesSection } from '@/features/design-system/verified-libraries/verified-libraries-section'
import { VocabularyPanel } from '@/features/design-system/vocabulary/vocabulary-panel'
import {
  evaluatePreconditions,
  type PreconditionReport,
  type ScreenContract,
} from '@/features/ux-contract/screen-contract'
import { STUDIO_DICTIONARIES } from '@/i18n/studio-dictionaries'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { cn } from '@/lib/utils'
import { getLocale } from '@/pages/cogo-marketing/i18n/locale-store'
import { useStudioStore } from '@/store/studio-store'
import { AgenticTabs } from './agentic-tabs'
import { isAgenticDesignSystemTab, type AgenticDesignSystemTab } from './types'
import { DesignDirectionChooser } from './design-direction-chooser'
import { getDesignDirectionCopy } from './design-direction-copy'

const DESIGN_CONTEXT_COPY = {
  en: {
    sourceTitle: 'Project design source',
    selectionTitle: 'Editor selection',
    reading: 'Reading the open project’s source…',
    noProject: 'No project is open. The workspace theme editor is available without a project source.',
    noTokens: (name: string) => `${name} is open, but no imported design variables were found.`,
    sourceReady: (name: string, source: string, count: number) => `${name} · ${source || 'Imported source'} · ${count} variables`,
    sourceError: 'The project source could not be read. No source-derived value is claimed.',
    currentTheme: 'Current unsaved theme',
    selectedSystem: (name: string) => `Saved system: ${name}`,
    noBlock: 'No UI Block selected for the live preview',
    selectedBlock: (category: string, blockId: string) => `UI Block: ${category}/${blockId}`,
  },
  ko: {
    sourceTitle: 'Project 디자인 소스',
    selectionTitle: '편집기 선택',
    reading: '열린 Project의 소스를 읽는 중…',
    noProject: '열린 Project가 없습니다. Project 소스 없이도 워크스페이스 테마 편집기는 사용할 수 있습니다.',
    noTokens: (name: string) => `${name}이 열려 있지만 가져온 디자인 변수를 찾지 못했습니다.`,
    sourceReady: (name: string, source: string, count: number) => `${name} · ${source || '가져온 소스'} · 변수 ${count}개`,
    sourceError: 'Project 소스를 읽지 못했습니다. 소스에서 파생된 값은 표시하지 않습니다.',
    currentTheme: '현재 저장되지 않은 테마',
    selectedSystem: (name: string) => `저장된 시스템: ${name}`,
    noBlock: '라이브 미리보기에 선택된 UI Block 없음',
    selectedBlock: (category: string, blockId: string) => `UI Block: ${category}/${blockId}`,
  },
  ja: {
    sourceTitle: 'Project デザインソース',
    selectionTitle: 'エディターの選択',
    reading: '開いている Project のソースを読み込み中…',
    noProject: '開いている Project はありません。Project ソースがなくてもワークスペースのテーマエディターは利用できます。',
    noTokens: (name: string) => `${name} は開いていますが、インポート済みデザイン変数が見つかりません。`,
    sourceReady: (name: string, source: string, count: number) => `${name} · ${source || 'インポート済みソース'} · 変数 ${count} 件`,
    sourceError: 'Project ソースを読み込めませんでした。ソース由来の値は表示しません。',
    currentTheme: '現在の未保存テーマ',
    selectedSystem: (name: string) => `保存済みシステム: ${name}`,
    noBlock: 'ライブプレビュー用の UI Block は未選択です',
    selectedBlock: (category: string, blockId: string) => `UI Block: ${category}/${blockId}`,
  },
  zh: {
    sourceTitle: 'Project 设计来源',
    selectionTitle: '编辑器选择',
    reading: '正在读取打开的 Project 来源…',
    noProject: '当前没有打开的 Project。即使没有 Project 来源，也可以使用工作区主题编辑器。',
    noTokens: (name: string) => `${name} 已打开，但未找到导入的设计变量。`,
    sourceReady: (name: string, source: string, count: number) => `${name} · ${source || '已导入来源'} · ${count} 个变量`,
    sourceError: '无法读取 Project 来源，因此不显示任何来源派生值。',
    currentTheme: '当前未保存主题',
    selectedSystem: (name: string) => `已保存系统：${name}`,
    noBlock: '尚未为实时预览选择 UI Block',
    selectedBlock: (category: string, blockId: string) => `UI Block：${category}/${blockId}`,
  },
  'zh-TW': {
    sourceTitle: 'Project 設計來源',
    selectionTitle: '編輯器選擇',
    reading: '正在讀取開啟的 Project 來源…',
    noProject: '目前沒有開啟的 Project。即使沒有 Project 來源，也可以使用工作區主題編輯器。',
    noTokens: (name: string) => `${name} 已開啟，但找不到匯入的設計變數。`,
    sourceReady: (name: string, source: string, count: number) => `${name} · ${source || '已匯入來源'} · ${count} 個變數`,
    sourceError: '無法讀取 Project 來源，因此不顯示任何來源衍生值。',
    currentTheme: '目前未儲存主題',
    selectedSystem: (name: string) => `已儲存系統：${name}`,
    noBlock: '尚未為即時預覽選擇 UI Block',
    selectedBlock: (category: string, blockId: string) => `UI Block：${category}/${blockId}`,
  },
} as const

/**
 * This screen's `ScreenContract` (Task 5, ontology-ux-contract cycle) — the
 * second consumer of the module Task 1 built, after
 * `generation-readiness/readiness-panel.tsx`'s `GENERATION_READINESS_SCREEN_
 * CONTRACT`. Exported so Task 6's conformance suite can walk it.
 *
 * `preconditions` deliberately does NOT check for an open project, even
 * though the design doc's default plan for this screen said "preconditions =
 * open project". Reading the actual data dependencies here says otherwise:
 *
 *   - `useDesignSystemInventory()` below lists THIS WORKSPACE's design
 *     systems (`design-system-inventory.ts`) — never scoped to a project.
 *   - `useSourceDerivedDesignSystem()` (this file) resolves to
 *     `emptySourceDerived('unsaved')` — a full, valid, all-gap
 *     `SourceDerivedDesignSystem` — when no project is open.
 *     `loadCurrentProjectSourceTokens()` only ever throws when a project HAS
 *     a registered source that fails to load right now; "no project" and "no
 *     source" both resolve to an empty token snapshot, not a throw (see that
 *     file's own doc comment).
 *   - `useDesignSystem()` (the theme editor driving `ThemeEditorPanel`) reads
 *     `useStudioStore().designSystem` — a workspace-level 6-axis slice, not
 *     project state.
 *
 * So this screen already renders a complete, honest, non-broken surface with
 * no project open: workspace inventory + all 6 axes shown as gaps + a
 * functioning theme editor. Gating it behind "open a project first" would be
 * exactly the "lock a working screen behind a contract" mistake the design
 * doc itself warns against.
 *
 * What this screen DOES depend on is identity: "this workspace's design
 * systems" and "save to Library" are meaningless without a signed-in user —
 * an anonymous visitor would see nobody's inventory, not a smaller version of
 * their own. `App.tsx`'s own `/studio/*` session gate (`if (!session &&
 * (!import.meta.env.DEV || forceAuth || explicitSignOut)) return <AuthPage
 * />`) intentionally does NOT fire in a plain dev server with a fresh
 * session — that condition is there for local DX, not disabled by mistake —
 * so in dev this precondition is not mere belt-and-suspenders: it is, in
 * practice, the ONLY thing standing between an anonymous visitor and this
 * screen (verified live — an unauthenticated `pnpm exec vite` session reaches
 * the full dashboard shell around this component; only this contract's own
 * check keeps the body from rendering). In a real signed-out production
 * visit the shell gate fires first and this component never mounts at all —
 * there this precondition is the redundant, self-contained belt-and-suspenders
 * layer it was originally written to be. Either way it is never a NEW
 * restriction on an authenticated user: it only ever affects the
 * signed-out case.
 *
 * `intent` and `states[*].describes` below are DEVELOPER-FACING contract
 * prose, and are left in Korean deliberately: nothing renders them. The
 * only readers are `ux-contract/contract-registry.ts` and the conformance
 * suite (`src/__tests__/ux-contract-conformance.test.ts`), which checks that
 * every `states` key is a borrowed statusIntent name and that every
 * precondition's `explanation` is non-empty — never what any of them say.
 * `preconditions[0].explanation` is in the same position for the same
 * reason: `PreconditionReport` carries it, but `DesignSystemPreconditionBlocked`
 * renders only `reason` + `recovery`, so no screen in this app shows it.
 * (That gap is worth naming rather than papering over: `Precondition`'s own
 * JSDoc calls `explanation` "사용자가 읽는 한 문장" and the conformance suite
 * calls it "the sentence the user reads when the screen refuses to open" —
 * two declarations that no consumer honours. Translating it would only make
 * an unread field prettier; giving it a reader is product work, not this.)
 *
 * `reason` and `recovery.label` ARE rendered, and so they are read out of
 * the active locale's dictionary. `check()` runs outside the component tree
 * (it is called from an effect, through `evaluatePreconditions`), so it uses
 * `STUDIO_DICTIONARIES[getLocale()]` at producer time — the same convention
 * as `store/project-load-error-copy.ts` and
 * `features/chat/services/chat-model-adapter.ts`, and as this feature's own
 * `verified-libraries/verified-libraries-copy.ts`.
 */
export const DESIGN_SYSTEM_SCREEN_CONTRACT: ScreenContract = {
  screenId: 'design-system',
  intent:
    '이 워크스페이스가 가진 디자인시스템을 보여주고, 소스에서 읽은(또는 아직 비어 있는) 6축을 정해 Library 에 저장할 수 있게 한다.',
  preconditions: [
    {
      id: 'authenticated',
      explanation:
        '이 화면은 로그인한 사용자의 워크스페이스 — 디자인시스템 목록과 저장 — 를 보여줍니다. 로그인 없이는 누구의 것도 아닌 화면이 됩니다.',
      check: async () => {
        const { session } = useStudioStore.getState()
        if (!session) {
          const copy = STUDIO_DICTIONARIES[getLocale()].designSystem.precondition
          return {
            ok: false,
            reason: copy.signedOutReason,
            recovery: { label: copy.signInCta, navigateTo: '/login' },
          }
        }
        return { ok: true, evidence: 'session present' }
      },
    },
  ],
  states: {
    pending: { describes: '로그인 상태를 확인하고 있습니다.' },
    success: {
      describes:
        '이 워크스페이스의 디자인시스템 인벤토리, 소스에서 읽은 6축, 테마 편집기를 보여줍니다.',
    },
  },
}

/**
 * Runs `DESIGN_SYSTEM_SCREEN_CONTRACT`'s preconditions once on mount.
 * `null` = not settled yet (the very first render, before the microtask the
 * check resolves on) — the screen shows neither its body nor a recovery
 * screen while this is `null`, since neither claim ("you may proceed" /
 * "you may not") is true yet.
 */
function useDesignSystemPreconditions(): PreconditionReport | null {
  const [report, setReport] = useState<PreconditionReport | null>(null)
  // `check()` builds its sentences at producer time from
  // `STUDIO_DICTIONARIES[getLocale()]`, so the report is locale-bearing state.
  // Running it once on mount froze the reason and the recovery label into
  // whatever language was active then: switching language while standing on
  // the gate flipped the heading (`useStudioLocale` is a
  // `useSyncExternalStore`) and left the reason and the CTA behind — an
  // English heading over a Korean sentence, measured.
  //
  // The previous report is deliberately NOT cleared while the new one
  // resolves: `null` means "neither claim is true yet" and would drop the
  // reader back to the checking frame on every language change.
  const { locale } = useStudioLocale()

  useEffect(() => {
    let cancelled = false
    void evaluatePreconditions(DESIGN_SYSTEM_SCREEN_CONTRACT).then((result) => {
      if (!cancelled) setReport(result)
    })
    return () => {
      cancelled = true
    }
  }, [locale])

  return report
}

/**
 * `/studio/design-system` — the screen a person opens when they want to work
 * on the look of what they make.
 *
 * It used to open on "Design System Control Plane": four tinted `pass` cards,
 * a ten-row proof-gate table with nine green passes, a promotion queue and a
 * chunk lifecycle — all of it read out of `agentic/fixtures.ts`, a frozen
 * snapshot of an internal evaluation run dated 2026-06-21. It said the same
 * nine passes to a workspace that had never saved a design system. Those
 * panels are gone; what is left either reads live data or says what it cannot
 * say. See `design-system-inventory.ts` for the repair this follows.
 */
export function AgenticDesignSystemPage() {
  const { strings } = useStudioLocale()
  const dsCopy = strings.designSystem
  // FINDING-004: the active tab is part of "where am I" — it now reads from
  // and writes to `?tab=` so the Ontology/Validation/Vocabulary surfaces can
  // be linked, bookmarked, and asserted by URL instead of click-path only.
  const [activeTab, setActiveTabState] = useState<AgenticDesignSystemTab>(() => {
    if (typeof window === 'undefined') return 'systems'
    const requested = new URLSearchParams(window.location.search).get('tab')
    return requested !== null && isAgenticDesignSystemTab(requested)
      ? requested
      : 'systems'
  })
  const setActiveTab = useCallback((tab: AgenticDesignSystemTab) => {
    setActiveTabState(tab)
    if (typeof window === 'undefined') return
    const url = new URL(window.location.href)
    if (tab === 'systems') url.searchParams.delete('tab')
    else url.searchParams.set('tab', tab)
    window.history.replaceState(window.history.state, '', url)
  }, [])
  const { summary, systems, retry: retryInventory } = useDesignSystemInventory()
  const preconditionReport = useDesignSystemPreconditions()

  if (!preconditionReport) {
    return (
      <div
        data-testid="agentic-design-system-page"
        data-contract-state="pending"
        className="flex h-full min-h-0 items-center justify-center bg-background text-foreground"
      >
        <p className="text-sm text-muted-foreground">{strings.designSystem.precondition.checkingReason}</p>
      </div>
    )
  }

  if (!preconditionReport.ok) {
    return <DesignSystemPreconditionBlocked report={preconditionReport} />
  }

  return (
    // Reported defect: this container carried `min-w-[760px]`, so on a 390px
    // phone the whole page was laid out 760px wide inside a 390px clip — the
    // card body copy, the tab strip, and the Validation Lab / Theme editor
    // buttons were all sliced off at the right edge with no ellipsis and no
    // way to scroll to them.
    <div
      data-testid="agentic-design-system-page"
      data-contract-state="ready"
      className="flex h-full min-h-0 flex-col bg-background text-foreground"
    >
      <header className="border-b border-border px-5 py-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <h1 data-route-heading="design-system" className="text-2xl font-semibold tracking-tight">
              {dsCopy.title}
            </h1>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">
              {dsCopy.subtitle}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('validation')}
              className="inline-flex h-10 items-center gap-2 rounded-lg border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              <TestTube2 className="h-4 w-4" />
              {dsCopy.validationLab}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('systems')}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {dsCopy.themeEditor}
            </button>
          </div>
        </div>
      </header>

      <AgenticTabs activeTab={activeTab} onSelect={setActiveTab} />

      <div className="min-h-0 flex-1 overflow-auto">
        {activeTab === 'systems' && (
          <SystemsPanel
            summary={summary}
            systems={systems}
            onRetryInventory={retryInventory}
          />
        )}
        {activeTab === 'validation' && <ValidatorPanel />}
        {activeTab === 'vocabulary' && (
          <div className="p-5">
            <VocabularyPanel />
          </div>
        )}
        {activeTab === 'briefings' && (
          <div className="h-[760px] min-h-0">
            <BriefingPanel />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * What renders instead of the screen body when
 * `DESIGN_SYSTEM_SCREEN_CONTRACT`'s preconditions are not met — reason +
 * recovery, per the design doc's central rule: "메뉴로 연결되면 무조건
 * 동작하면 안 된다" (a screen must not just run because a menu links to it).
 *
 * Same tone rule as everywhere else on this screen (`inventoryToneClassName`
 * above, the module doc on `AxisResolutionList`): attention only gets a
 * colour for a genuine failure. Not being signed in yet is not a failure —
 * it is an ordinary, expected, fully-recoverable state — so this renders in
 * the same neutral `text-foreground`/`text-muted-foreground` pair the rest
 * of the screen uses, never `text-destructive`.
 */
function DesignSystemPreconditionBlocked({ report }: { report: PreconditionReport }) {
  const { strings } = useStudioLocale()
  const failed = report.verdicts.find((verdict) => !verdict.ok)
  const reason =
    failed && !failed.ok
      ? failed.reason
      : strings.designSystem.precondition.unmetFallbackReason
  const recovery = failed && !failed.ok ? failed.recovery : undefined

  return (
    <div
      data-testid="agentic-design-system-page"
      data-contract-state="blocked"
      className="flex h-full min-h-0 flex-col items-center justify-center gap-4 bg-background px-6 py-16 text-center text-foreground"
    >
      <h1 data-route-heading="design-system" className="text-lg font-semibold">{strings.designSystem.title}</h1>
      <p
        data-testid="design-system-precondition-reason"
        className="max-w-md text-sm leading-6 text-muted-foreground"
      >
        {reason}
      </p>
      {recovery ? (
        <a
          href={recovery.navigateTo}
          data-testid="design-system-precondition-recovery"
          className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
        >
          {recovery.label}
        </a>
      ) : null}
    </div>
  )
}

function SystemsPanel({
  summary,
  systems,
  onRetryInventory,
}: {
  summary: DesignSystemInventorySummary
  systems: readonly DesignSystem[]
  onRetryInventory: () => void
}) {
  const { derived, loadError, status: sourceStatus } = useSourceDerivedDesignSystem()
  const { setOption, setBulk } = useDesignSystem()
  const { locale } = useStudioLocale()
  const directionCopy = getDesignDirectionCopy(locale)
  const contextCopy = DESIGN_CONTEXT_COPY[locale]
  const currentProjectId = useStudioStore((s) => s.currentProjectId)
  const currentProjectName = useStudioStore((s) => s.currentProjectName)
  const [selectedPreviewBlock, setSelectedPreviewBlock] = useState<UiBlockRef | null>(null)
  const [selectedSystem, setSelectedSystem] = useState<Pick<DesignSystem, 'id' | 'name'> | null>(null)
  // Build-time registry read (fail-closed): the manifest is bundled, so the
  // read is synchronous and its result cannot change within a session.
  const verifiedLibrariesRead = useMemo(() => readVerifiedDesignLibraries(), [])

  useEffect(() => {
    if (!selectedSystem) return
    const loadedAxes = { ...useStudioStore.getState().designSystem }
    return useStudioStore.subscribe((state) => {
      if (AXIS_KEYS.some((axis) => state.designSystem[axis] !== loadedAxes[axis])) {
        setSelectedSystem(null)
      }
    })
  }, [selectedSystem])

  return (
    <div className="space-y-4 p-5">
      <section
        data-testid="design-system-context-status"
        className="grid gap-3 lg:grid-cols-3"
      >
        <InventorySummaryCard summary={summary} onRetry={onRetryInventory} />
        <div className="rounded-lg border border-border bg-card px-4 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {contextCopy.sourceTitle}
          </h2>
          <p
            className={cn(
              'mt-2 text-xs leading-5',
              sourceStatus === 'error' ? 'text-destructive' : 'text-muted-foreground',
            )}
            role={sourceStatus === 'error' ? 'alert' : 'status'}
          >
            {sourceStatus === 'loading'
              ? contextCopy.reading
              : sourceStatus === 'error'
                ? contextCopy.sourceError
                : !currentProjectId
                  ? contextCopy.noProject
                  : derived.source.variableCount > 0
                    ? contextCopy.sourceReady(
                        currentProjectName ?? currentProjectId,
                        derived.source.label,
                        derived.source.variableCount,
                      )
                    : contextCopy.noTokens(currentProjectName ?? currentProjectId)}
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card px-4 py-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {contextCopy.selectionTitle}
          </h2>
          <p className="mt-2 text-xs font-medium leading-5 text-foreground">
            {selectedSystem
              ? contextCopy.selectedSystem(selectedSystem.name)
              : contextCopy.currentTheme}
          </p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            {selectedPreviewBlock
              ? contextCopy.selectedBlock(
                  selectedPreviewBlock.category,
                  selectedPreviewBlock.blockId,
                )
              : contextCopy.noBlock}
          </p>
        </div>
      </section>
      <DesignDirectionChooser />
      <UiBlockWorkbench
        selectedPreview={selectedPreviewBlock}
        onPreview={setSelectedPreviewBlock}
      />
      {/* The rows the banner above counts — same read, so number and list
          can only agree. Compact by default (progressive disclosure); one
          action per row loads the saved snapshot into the theme editor. */}
      <WorkspaceDesignSystemsList
        systems={systems}
        onSelectSystem={(system) => setSelectedSystem({ id: system.id, name: system.name })}
        onApply={(axes) => {
          const state = parseDesignSystemState(axes)
          if (state) setBulk(state)
        }}
      />
      {/* The DESIGN leads: the live block-grid preview (real cogo components
          re-rendering as axes change) is this page's reason to exist — it
          used to sit below the all-text axis wall, so the screen read as
          text-only. The source-derived axis truth panel follows it. */}
      <ThemeEditorPanel previewBlock={selectedPreviewBlock} />
      <details
        className="group rounded-xl border border-border bg-card"
        data-testid="design-system-advanced-details"
      >
        <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 marker:content-none">
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-foreground">{directionCopy.advanced}</span>
            <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{directionCopy.advancedDetail}</span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden />
        </summary>
        <div className="space-y-4 border-t border-border p-4">
          {loadError ? <SourceLoadErrorNote message={loadError} /> : null}
          <AxisResolutionList
            derived={derived}
            onChoose={(axis: AxisName, value: string) => {
              setOption(axis, value as DesignSystemState[typeof axis])
            }}
          />
          <VerifiedLibrariesSection read={verifiedLibrariesRead} />
          <ProjectTokensNote />
        </div>
      </details>
    </div>
  )
}

/**
 * All 6 axes, all `gap`, no source — what `deriveAxes`/`adviseAxes` compute
 * for an empty token list. Used as the pre-load placeholder so the axis list
 * is never absent from this screen, even for the instant before the real
 * read resolves (or if it never does — see `useSourceDerivedDesignSystem`).
 */
function emptySourceDerived(
  projectId: string,
  copy?: DesignAxesCopy,
): SourceDerivedDesignSystem {
  return {
    projectId,
    source: { kind: 'figma', label: '', variableCount: 0 },
    axes: adviseAxes(deriveAxes([], copy), copy),
    derivedAt: new Date(0).toISOString(),
  }
}

/**
 * Reads the open project's imported design source through the capability
 * `source-derived/register-source-derived-tools.ts` registers on the
 * Studio MCP bus (wired in `src/mcp/register-studio-tools.ts`) — called
 * directly here rather than through the bus, since this is the same
 * in-process React tree, not an external tool caller.
 *
 * `derived` is always a complete `SourceDerivedDesignSystem`, never null:
 * it starts as the all-gap placeholder above and is replaced once the real
 * read resolves, so the axis list never has a moment where it has nothing
 * to render. A failed read (source registered but unreadable right now,
 * per `load-current-project-source-tokens.ts`) surfaces as `loadError`
 * without pulling the axes off screen — the gap state and a load failure
 * are different facts and this keeps them visibly different.
 */
function useSourceDerivedDesignSystem(): {
  derived: SourceDerivedDesignSystem
  loadError: string | null
  status: 'loading' | 'ready' | 'error'
} {
  const projectId = useStudioStore((s) =>
    s.projectSource === 'server' ? s.currentProjectId : null,
  )
  const { strings } = useStudioLocale()
  const axesCopy = strings.designSystemAxes
  const [derived, setDerived] = useState<SourceDerivedDesignSystem>(() =>
    emptySourceDerived(projectId ?? 'unsaved', axesCopy),
  )
  const [loadError, setLoadError] = useState<string | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    setLoadError(null)
    setStatus('loading')
    setDerived(emptySourceDerived(projectId ?? 'unsaved', axesCopy))
    void deriveFromSource({
      projectId: projectId ?? 'unsaved',
      loadTokens: loadCurrentProjectSourceTokens,
      copy: axesCopy,
    }).then(
      (result) => {
        if (!cancelled) {
          setDerived(result)
          setStatus('ready')
        }
      },
      (error: unknown) => {
        if (cancelled) return
        setLoadError(
          error instanceof Error
            ? error.message
            : STUDIO_DICTIONARIES[getLocale()].designSystem.sourceRead.failed,
        )
        setStatus('error')
      },
    )
    return () => {
      cancelled = true
    }
  }, [projectId, axesCopy])

  return { derived, loadError, status }
}

/**
 * The one place on this screen attention gets a colour — same rule as
 * `inventoryToneClassName` below: this fires only for a genuine read
 * failure, never for an axis being an unresolved gap.
 */
export function SourceLoadErrorNote({ message }: { message: string }) {
  const { strings } = useStudioLocale()
  return (
    <section
      data-testid="source-derived-load-error"
      className="rounded-lg border border-destructive/30 bg-card px-4 py-3 text-xs text-destructive"
    >
      {strings.designSystem.sourceRead.failedWithMessage.replace('{message}', message)}
    </section>
  )
}

/**
 * Attention is the only tone that gets a colour. Nothing here earns a
 * success/confirmation colour: the card reports a count and the time it was
 * read, and a colour that reads "all good" would re-introduce exactly the
 * claim the counts avoid.
 */
function inventoryToneClassName(tone: DesignSystemInventorySummary['tone']): string {
  return tone === 'unreadable'
    ? 'border-destructive/30 text-destructive'
    : 'border-border text-muted-foreground'
}

/**
 * Exported for the accessibility gate
 * (`i18n/__tests__/accessibility-surface-localized.test.tsx`), which must
 * render this card to read its accessible NAME. The page around it is behind
 * a signed-in precondition that a jsdom harness cannot satisfy, and a section
 * whose only words are an `aria-label` cannot be asserted through a surface
 * that never mounts it.
 */
export function InventorySummaryCard({
  summary,
  onRetry,
}: {
  summary: DesignSystemInventorySummary
  onRetry?: () => void
}) {
  const { strings } = useStudioLocale()
  return (
    <section
      data-testid="design-system-inventory-summary"
      aria-label={strings.a11y.designSystemInventory}
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Boxes className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span
          data-testid="design-system-inventory-badge"
          data-tone={summary.tone}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium',
            inventoryToneClassName(summary.tone),
          )}
        >
          {summary.tone === 'unreadable' ? (
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          ) : null}
          {summary.badge}
        </span>
      </div>
      <p className="mt-2 text-xs leading-5 text-muted-foreground">
        {summary.detail}
      </p>
      {summary.tone === 'unreadable' && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-3 inline-flex min-h-10 items-center rounded-lg border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted"
        >
          {strings.designSystem.vocabularyPanel.retry}
        </button>
      ) : null}
    </section>
  )
}

function ThemeEditorPanel({ previewBlock }: { previewBlock: UiBlockRef | null }) {
  const { locale, strings } = useStudioLocale()
  const directionCopy = getDesignDirectionCopy(locale)
  const uiBlockCopy = strings.designSystem.uiBlocks
  const previewBlockRefs = useMemo(
    () => (previewBlock ? [previewBlock] : undefined),
    [previewBlock],
  )
  return (
    <section
      data-testid="design-system-theme-editor"
      className="overflow-hidden rounded-lg border border-border bg-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Palette className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <h2 className="text-sm font-semibold text-foreground">
              {directionCopy.fineTune}
            </h2>
          </div>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-muted-foreground">
            {directionCopy.fineTuneDetail}
          </p>
        </div>
        {previewBlock ? (
          <div className="inline-flex min-h-9 items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-3 text-xs text-muted-foreground">
            <Eye className="h-3.5 w-3.5 text-primary" aria-hidden />
            <span>{uiBlockCopy.readOnly}</span>
            <code className="font-medium text-foreground">
              {previewBlock.category}/{previewBlock.blockId}
            </code>
          </div>
        ) : null}
      </div>
      <div className="h-[540px] min-h-0 bg-background">
        {/* The panel header above is this editor's name here — see
            `DesignSystemPage`'s `showHeading` for why it must not print its
            own. */}
        <DesignSystemWorkbench
          showHeading={false}
          previewBlockRefs={previewBlockRefs}
        />
      </div>
    </section>
  )
}

/**
 * Where the other design-token surface lives. Tokens imported from a design
 * file belong to the project that imported them, so they cannot appear in a
 * workspace-level list — but nothing on this screen used to say where they
 * were, and a person looking for "my design system" had no way to find out.
 */
function ProjectTokensNote() {
  const { strings } = useStudioLocale()
  const copy = strings.designSystem.projectTokens
  return (
    <section
      data-testid="design-system-project-tokens-note"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <h2 className="text-sm font-semibold text-foreground">{copy.title}</h2>
      <p className="mt-1 max-w-3xl text-xs leading-5 text-muted-foreground">
        {copy.bodyBefore}
        <span className="font-medium text-foreground">{copy.navPath}</span>
        {copy.bodyAfter}
      </p>
    </section>
  )
}
